/**
 * MapLibre GL JS engine — initializes the 2D map with dark basemap.
 *
 * Style resolution is delegated to @/config/basemap which supports
 * PMTiles (Protomaps), OpenFreeMap, CARTO, and an inline fallback.
 */

import maplibregl from 'maplibre-gl';
import { appStore } from '@/core/store';
import { eventBus } from '@/core/events';
import { resolveBasemapStyle, initPMTilesProtocol } from '@/config/basemap';

let map: maplibregl.Map | null = null;

export interface MapEngineOptions {
  container: HTMLElement;
}

/** Initialize the MapLibre map engine. */
export async function initMapEngine(options: MapEngineOptions): Promise<maplibregl.Map> {
  initPMTilesProtocol(maplibregl);

  const { viewport } = appStore.getState();
  const style = await resolveBasemapStyle();

  map = new maplibregl.Map({
    container: options.container,
    style,
    center: [viewport.center[0], viewport.center[1]],
    zoom: viewport.zoom,
    renderWorldCopies: false,
    attributionControl: false,
    // Match Worldmonitor: request discrete GPU, no expensive antialiasing
    // antialias: false (default) — MSAA is expensive and not needed with deck.gl overlay
    // preserveDrawingBuffer: false (default) — allows canvas swap chain optimization
    canvasContextAttributes: { powerPreference: 'high-performance' },
    // Lock to flat mode — no pitch/rotation, simpler rendering pipeline
    maxPitch: 0,
    pitchWithRotate: false,
    dragRotate: false,
    touchPitch: false,
  });

  map.addControl(new maplibregl.AttributionControl({ compact: true }), 'bottom-right');

  // Override basemap background to tactical green-black after style loads
  map.on('style.load', () => {
    if (!map) return;
    try {
      map.setPaintProperty('background', 'background-color', '#080808');
    } catch {
      // Background layer may not exist in all styles — safe to ignore
    }
  });

  // Viewport sync
  map.on('moveend', () => {
    if (!map) return;
    const center = map.getCenter();
    const zoom = map.getZoom();
    appStore.setState({
      viewport: {
        ...appStore.getState().viewport,
        center: [center.lng, center.lat],
        zoom,
        bearing: 0,
        pitch: 0,
      },
    });
    eventBus.emit('map:viewChanged', {
      center: [center.lng, center.lat],
      zoom,
    });
  });

  map.on('click', (e) => {
    eventBus.emit('map:click', { lat: e.lngLat.lat, lng: e.lngLat.lng });
  });

  // WebGL context loss recovery
  const canvas = map.getCanvas();
  if (canvas) {
    canvas.addEventListener('webglcontextlost', (e) => {
      e.preventDefault();
      console.warn('[MapEngine] WebGL context lost — pausing render');
    });
    canvas.addEventListener('webglcontextrestored', () => {
      console.info('[MapEngine] WebGL context restored — resuming');
    });
  }

  return map;
}

/** Get the current map instance. */
export function getMap(): maplibregl.Map | null {
  return map;
}

// Debug: expose map for development inspection
if (import.meta.env.DEV) {
  (window as unknown as Record<string, unknown>).__SENTINEL_MAP__ = { getMap: () => map };
}

/** Destroy the map instance. */
export function destroyMap(): void {
  if (map) {
    map.remove();
    map = null;
  }
}
